import React from 'react';
import { Container, Typography, Box } from '@mui/material';

const TermsAndConditions = () => {
  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Terms and Conditions for Amazon Dropshipping
        </Typography>
        <Typography variant="body1" paragraph>
          These Terms and Conditions ("Agreement") govern your use of Amazon Dropshipping services, including listing and selling products on the Amazon platform through the dropshipping model. By accessing or using our services, you ("User," "Seller," or "you") agree to be bound by these Terms and Conditions. If you do not agree with these terms, you may not use our services.
        </Typography>
        <Typography variant="body1" paragraph>
          Please read these terms carefully before engaging with Amazon Dropshipping services. By using the services, you confirm that you understand and agree to comply with these terms.
        </Typography>

        <Typography variant="h6" gutterBottom>
          1. Services Provided
        </Typography>
        <Typography variant="body1" paragraph>
          Amazon Dropshipping offers an e-commerce solution that allows entrepreneurs to sell products on Amazon without holding inventory. As a seller, you can list products from third-party suppliers on Amazon’s platform and, upon receiving an order, the supplier ships the product directly to the customer. This eliminates the need for warehousing and reduces upfront investment costs.
        </Typography>
        <Typography variant="body1" paragraph>
          We provide you with tools, resources, and guidance to help you succeed in the dropshipping business, including access to reliable suppliers, order management solutions, and customer service support.
        </Typography>

        <Typography variant="h6" gutterBottom>
          2. Eligibility
        </Typography>
        <Typography variant="body1" paragraph>
          To use Amazon Dropshipping services, you must be at least 18 years of age or the age of majority in your jurisdiction. By accepting these Terms and Conditions, you confirm that you are eligible to engage in this Agreement. You further agree to provide accurate and complete information during the registration process and to keep your account details up to date.
        </Typography>

        <Typography variant="h6" gutterBottom>
          3. Account Registration
        </Typography>
        <Typography variant="body1" paragraph>
          In order to use Amazon Dropshipping services, you must create an account. During the registration process, you will be required to provide certain personal information, including but not limited to your name, contact details, payment information, and tax identification information. You are responsible for maintaining the confidentiality of your account credentials, including your username and password.
        </Typography>
        <Typography variant="body1" paragraph>
          Any activity under your account, including unauthorized use or actions taken by third parties, will be your responsibility. If you suspect that your account has been compromised, you must immediately notify us.
        </Typography>

        <Typography variant="h6" gutterBottom>
          4. Product Listings
        </Typography>
        <Typography variant="body1" paragraph>
          As a user of Amazon Dropshipping services, you are solely responsible for the accuracy, legality, and compliance of your product listings. This includes ensuring that all product information is truthful and up to date, and that the products you sell do not infringe on third-party intellectual property rights or violate Amazon’s policies.
        </Typography>
        <Typography variant="body1" paragraph>
          You must adhere to all Amazon guidelines and any applicable laws governing the sale of goods, including those relating to product safety, labeling, and advertising.
        </Typography>

        <Typography variant="h6" gutterBottom>
          5. Fees and Payments
        </Typography>
        <Typography variant="body1" paragraph>
          By using Amazon Dropshipping, you acknowledge and agree to pay all applicable fees associated with using the service. These fees may include, but are not limited to, service fees, transaction fees, subscription fees, advertising costs, and other fees that are outlined in the pricing section of our platform.
        </Typography>
        <Typography variant="body1" paragraph>
          All fees will be deducted from your earnings in your wallet balance, and you agree to maintain sufficient funds in your wallet to cover these costs. Amazon Dropshipping reserves the right to change fee structures at any time, with prior notice to users.
        </Typography>

        <Typography variant="h6" gutterBottom>
          6. Wallet and Earnings
        </Typography>
        <Typography variant="body1" paragraph>
          All proceeds from sales made on the Amazon platform through your dropshipping store will be credited to your wallet balance within the Amazon Dropshipping platform. You may request to withdraw funds from your wallet according to the terms outlined in these conditions. Withdrawals are subject to the availability of funds and other applicable conditions, such as payment verification processes.
        </Typography>

        <Typography variant="h6" gutterBottom>
          7. Prohibited Conduct
        </Typography>
        <Typography variant="body1" paragraph>
          You agree not to engage in the following prohibited activities while using Amazon Dropshipping services:
        </Typography>
        <Typography variant="body1" paragraph>
          • Fraudulent Activity: Engaging in any form of fraudulent activity, such as selling counterfeit products, misrepresenting product details, or falsifying sales data.
        </Typography>
        <Typography variant="body1" paragraph>
          • Violation of Amazon Policies: Disregarding Amazon’s Seller Policies and any other platform guidelines, or attempting to circumvent the established procedures for product listing and fulfillment.
        </Typography>
        <Typography variant="body1" paragraph>
          • Illegal or Harmful Products: Selling products that are illegal, unsafe, or prohibited, such as dangerous goods or items that violate intellectual property rights.
        </Typography>
        <Typography variant="body1" paragraph>
          • Spam or Misuse: Engaging in spamming, unauthorized advertising, or using the platform to promote third-party services without consent.
        </Typography>
        <Typography variant="body1" paragraph>
          • Manipulation of Platform Systems: Attempting to manipulate Amazon's ranking or sales algorithms, including review manipulation or other unfair practices.
        </Typography>
        <Typography variant="body1" paragraph>
          Violating these provisions may result in suspension or termination of your account, and you may be held liable for any damages caused.
        </Typography>

        <Typography variant="h6" gutterBottom>
          8. Termination of Services
        </Typography>
        <Typography variant="body1" paragraph>
          Amazon Dropshipping reserves the right to suspend, limit, or terminate your account at our discretion, particularly if we believe you have violated these Terms and Conditions, engaged in fraudulent activities, or caused harm to our platform, customers, or partners.
        </Typography>
        <Typography variant="body1" paragraph>
          In the event of account termination, you may forfeit any remaining balance in your wallet, and you will not be entitled to any refund or compensation. Upon termination, you agree to cease all use of Amazon Dropshipping services immediately.
        </Typography>

        <Typography variant="h6" gutterBottom>
          9. Liability and Disclaimers
        </Typography>
        <Typography variant="body1" paragraph>
          While Amazon Dropshipping strives to provide a reliable platform for sellers, we do not guarantee the success of your business or your sales. You acknowledge that the success of your dropshipping business depends on various factors, including market conditions, product demand, competition, and other variables that are beyond our control.
        </Typography>
        <Typography variant="body1" paragraph>
          We are not liable for any direct or indirect damages resulting from the use of Amazon Dropshipping services, including but not limited to system downtime, product issues, or financial losses.
        </Typography>

        <Typography variant="h6" gutterBottom>
          10. Indemnification
        </Typography>
        <Typography variant="body1" paragraph>
          You agree to indemnify and hold harmless Amazon Dropshipping, its affiliates, partners, and employees from any claims, losses, damages, or liabilities arising from your use of the services, including any violations of these Terms and Conditions or any third-party rights.
        </Typography>

        <Typography variant="h6" gutterBottom>
          11. Amendments
        </Typography>
        <Typography variant="body1" paragraph>
          Amazon Dropshipping reserves the right to modify or update these Terms and Conditions at any time, with prior notice to users. Any changes to the terms will be posted on the platform, and it is your responsibility to review the updated terms regularly. Continued use of the services after any such changes will constitute your acceptance of the modified terms.
        </Typography>

        <Typography variant="h6" gutterBottom>
          12. Governing Law
        </Typography>
        <Typography variant="body1" paragraph>
          This Agreement will be governed by and construed in accordance with the laws of [Country/State], without regard to its conflict of law principles. Any legal actions or proceedings related to this Agreement will be subject to the exclusive jurisdiction of the courts in [Location].
        </Typography>

        <Typography variant="body1" paragraph>
          By using Amazon Dropshipping services, you acknowledge that you have read, understood, and agree to these Terms and Conditions.
        </Typography>
      </Box>
    </Container>
  );
};

export default TermsAndConditions;